import { useCallback, useEffect, useLayoutEffect, useState } from "react";

export type ColorScheme = "light" | "dark";

const STORAGE_KEY = "@color-scheme";
const LIGHT_QUERY = "(prefers-color-scheme: light)";

const getSystemScheme = (): ColorScheme =>
	typeof window.matchMedia === "function" && window.matchMedia(LIGHT_QUERY).matches ? "light" : "dark";

const getStoredScheme = (): ColorScheme | null => {
	const stored = localStorage.getItem(STORAGE_KEY);
	return stored === "light" || stored === "dark" ? stored : null;
};

/**
 * Resolves the active colour scheme: the user's explicit choice if they have made
 * one, otherwise whatever the OS is asking for. The result is mirrored onto
 * <html data-theme> so stylesheets can key off it.
 */
const useColorScheme = () => {
	const [scheme, setScheme] = useState<ColorScheme>(() => getStoredScheme() ?? getSystemScheme());

	/*
	 * A layout effect, not a passive one: children that read the CSS variables
	 * this attribute switches (the topography canvas paints from them) do so in
	 * their own passive effects, and every layout effect runs before those.
	 */
	useLayoutEffect(() => {
		document.documentElement.setAttribute("data-theme", scheme);
	}, [scheme]);

	// Keep following the OS until the user picks a scheme for themselves.
	useEffect(() => {
		if (typeof window.matchMedia !== "function") {
			return;
		}
		const query = window.matchMedia(LIGHT_QUERY);
		const handleSystemChange = (event: MediaQueryListEvent) => {
			if (!getStoredScheme()) {
				setScheme(event.matches ? "light" : "dark");
			}
		};
		query.addEventListener("change", handleSystemChange);
		return () => query.removeEventListener("change", handleSystemChange);
	}, []);

	const toggleScheme = useCallback(() => {
		setScheme((current) => {
			const next: ColorScheme = current === "dark" ? "light" : "dark";
			localStorage.setItem(STORAGE_KEY, next);
			return next;
		});
	}, []);

	return { scheme, toggleScheme };
};

export default useColorScheme;
